import { PageHeader } from "@/components/page";
import { config as envConfig } from "@/config";
import { useGetAppConfig } from "@/hooks/queryHooks/configHooks";
import { capitalize } from "@repo/shared/utils";
import { Button } from "@repo/ui/components/Base/Button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@repo/ui/components/Base/Popover";
import { CheckCircleIcon } from "lucide-react";

const CopyButton = ({ text }: { text: string }) => {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => navigator.clipboard.writeText(text)}
        >
          {text}
        </Button>
      </PopoverTrigger>
      <PopoverContent>
        <CheckCircleIcon /> Copied to Clipboard!
      </PopoverContent>
    </Popover>
  );
};

interface NestedListProps {
  data: Record<string, string | Record<string, string>>;
}

const NestedList: React.FC<NestedListProps> = ({ data }) => {
  const renderList = (
    obj: Record<string, string | Record<string, string> | boolean>,
    depth: number
  ): JSX.Element[] => {
    return Object.keys(obj).map((key) => {
      const value = obj[key];

      if (typeof value === "object") {
        return (
          <li key={key} className="text-lg font-bold">
            {capitalize(key)}
            <ul className="pl-4 text-base font-normal">
              {renderList(value, depth + 1)}
            </ul>
          </li>
        );
      } else {
        return (
          <li key={key} className="my-auto text-middle">
            {`${key}: `}
            <CopyButton text={value.toString()} />
          </li>
        );
      }
    });
  };

  return <ul>{renderList(data, 0)}</ul>;
};

export const DebugPage = () => {
  const { data: config } = useGetAppConfig();

  return (
    <div className="flex flex-col flex-grow">
      <PageHeader title="Debug Page" />
      <h1 className="text-xl font-bold">Environment Variables</h1>
      <NestedList
        data={
          envConfig as unknown as Record<
            string,
            string | Record<string, string>
          >
        }
      />

      <h1 className="text-xl font-bold">Firestore Config</h1>
      {config && (
        <NestedList
          data={
            config as unknown as Record<string, string | Record<string, string>>
          }
        />
      )}
    </div>
  );
};
